import {
  useState, useEffect, useContext, useCallback,
} from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { getMyLeagueStatus, getLeagueLeaderboard, getLeagueTiers } from '../services/leagueApi';
import LeagueBadge, { TIER_CONFIG } from '../components/league/LeagueBadge';
import LeagueProgressBar from '../components/league/LeagueProgressBar';
import LeagueJoinCard from '../components/league/LeagueJoinCard';
import WeeklyResultsModal from '../components/league/WeeklyResultsModal';

const TIER_TABS = [
  { id: 'bronze', label: 'Bronze' },
  { id: 'silver', label: 'Silver' },
  { id: 'gold', label: 'Gold' },
  { id: 'platinum', label: 'Platinum' },
  { id: 'diamond', label: 'Diamond' },
];

function League() {
  const { user } = useContext(AuthContext);
  const [status, setStatus] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [tierCounts, setTierCounts] = useState({});
  const [selectedTier, setSelectedTier] = useState('bronze');
  const [isLoading, setIsLoading] = useState(true);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showScoringInfo, setShowScoringInfo] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      setIsLoading(true);
      const [statusData, tiersData] = await Promise.all([
        user ? getMyLeagueStatus() : Promise.resolve({ joined: false }),
        getLeagueTiers(),
      ]);
      setStatus(statusData);

      const counts = {};
      tiersData.tiers.forEach((t) => { counts[t.id] = t.playerCount; });
      setTierCounts(counts);

      // Default selected tier to user's tier
      if (statusData.joined && statusData.tier) {
        setSelectedTier(statusData.tier);
      }
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  const fetchLeaderboard = useCallback(async (tier) => {
    try {
      setLeaderboardLoading(true);
      const data = await getLeagueLeaderboard(tier);
      setLeaderboard(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLeaderboardLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    fetchLeaderboard(selectedTier);
  }, [selectedTier, fetchLeaderboard]);

  const getTimeRemaining = () => {
    if (!status?.weekEndsAt) return '';
    const now = new Date();
    const end = new Date(status.weekEndsAt);
    const diff = end - now;
    if (diff <= 0) return 'Processing...';
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    if (days > 0) return `${days}d ${hours}h remaining`;
    return `${hours}h remaining`;
  };

  const getRankDisplay = (rank) => (
    <span className="text-puzzle-text-secondary font-bold">{rank}</span>
  );

  const renderPlayerAvatar = (player) => (
    <div className="w-8 h-8 rounded-full bg-puzzle-muted overflow-hidden flex items-center justify-center flex-shrink-0">
      {player.avatar ? (
        <img
          src={player.avatar}
          alt={player.username}
          className="w-full h-full object-cover"
        />
      ) : (
        <span className="text-xs font-semibold text-puzzle-text-secondary">
          {player.username.slice(0, 2).toUpperCase()}
        </span>
      )}
    </div>
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-puzzle-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-puzzle-blue" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-puzzle-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-puzzle-danger mb-4">{error}</p>
          <button
            type="button"
            onClick={fetchStatus}
            className="text-puzzle-blue hover:underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  // Not joined yet
  if (!status?.joined) {
    return (
      <div className="min-h-screen bg-puzzle-background py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-puzzle-text mb-2">Leagues</h1>
            <p className="text-puzzle-text-secondary">Competitive ranked divisions</p>
          </div>
          <LeagueJoinCard onJoined={fetchStatus} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-puzzle-background py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Weekly Results Modal */}
        <WeeklyResultsModal recentHistory={status.recentHistory} />

        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-puzzle-text mb-2">Leagues</h1>
          <p className="text-puzzle-text-secondary">{getTimeRemaining()}</p>
        </div>

        {/* User Status Card */}
        <div className="bg-puzzle-surface rounded-xl shadow p-5 mb-6">
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <div className="text-center sm:text-left flex-shrink-0">
              <LeagueBadge tier={status.tier} size="lg" />
            </div>
            <div className="flex-1 w-full">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-puzzle-text-secondary">
                  This cycle:
                  {' '}
                  <span className="font-bold text-puzzle-text">
                    {status.weeklyRating}
                    {' '}
                    pts
                  </span>
                  {' '}
                  &middot;
                  {' '}
                  {status.weeklyCompletions}
                  {' '}
                  puzzle
                  {status.weeklyCompletions !== 1 ? 's' : ''}
                </span>
                <span className="text-sm text-puzzle-text-secondary">
                  All-time:
                  {' '}
                  <span className="font-semibold text-puzzle-text">
                    {status.currentRating}
                    {' '}
                    pts
                  </span>
                </span>
              </div>
              <LeagueProgressBar
                rankInTier={status.rankInTier}
                totalInTier={status.totalInTier}
                promotionZone={status.promotionZone}
                relegationZone={status.relegationZone}
              />
            </div>
          </div>

          {/* Promotion/Relegation indicators */}
          {status.promotionZone && status.nextTier && (
            <div className="mt-3 text-sm text-green-600 bg-green-50 dark:bg-green-900/20 rounded-lg px-3 py-2 text-center">
              You&apos;re in the promotion zone! Keep it up to reach
              {' '}
              {status.nextTier.icon}
              {' '}
              {status.nextTier.name}
              .
            </div>
          )}
          {status.relegationZone && status.prevTier && (
            <div className="mt-3 text-sm text-red-600 bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2 text-center">
              You&apos;re in the relegation zone. Complete more puzzles to stay in your tier!
            </div>
          )}
        </div>

        {/* How Scoring Works (collapsible) */}
        <div className="mb-6">
          <button
            type="button"
            onClick={() => setShowScoringInfo(!showScoringInfo)}
            className="text-sm text-puzzle-blue hover:underline flex items-center gap-1 mx-auto"
          >
            {showScoringInfo ? 'Hide' : 'How scoring works'}
            <span className={`transition-transform ${showScoringInfo ? 'rotate-180' : ''}`}>
              &#9660;
            </span>
          </button>
          {showScoringInfo && (
            <div className="mt-3 bg-puzzle-surface rounded-lg p-4 text-sm text-puzzle-text-secondary max-w-lg mx-auto">
              <p className="mb-2">Each daily puzzle completion earns rating points based on:</p>
              <ul className="list-disc list-inside space-y-1 ml-2 mb-3">
                <li>
                  <strong>Speed</strong>
                  {' '}
                  (up to 50 pts) &mdash; faster = more points
                </li>
                <li>
                  <strong>Efficiency</strong>
                  {' '}
                  (up to 50 pts) &mdash; fewer moves = more points
                </li>
                <li>
                  <strong>Completion bonus</strong>
                  {' '}
                  &mdash; 10 pts just for finishing
                </li>
                <li>
                  <strong>Streak multiplier</strong>
                  {' '}
                  &mdash; up to 20% bonus at 10+ day streak
                </li>
              </ul>
              <p className="mb-2">
                <strong>Example:</strong>
                {' '}
                70 pieces in 3 min (250 moves) or 12 min (90 moves)
                {' '}
                both &#8776; 86 pts with a 5-day streak
              </p>
              <p>
                Top 20% get promoted every two weeks, bottom 20% get relegated.
                {' '}
                <Link to="/how-it-works#leagues" className="text-puzzle-blue hover:underline">
                  Full details
                </Link>
              </p>
            </div>
          )}
        </div>

        {/* Tier Tabs */}
        <div className="flex justify-center mb-6 overflow-x-auto pb-2">
          <div className="inline-flex bg-puzzle-surface rounded-lg shadow p-1 gap-1">
            {TIER_TABS.map((t) => {
              const config = TIER_CONFIG[t.id];
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setSelectedTier(t.id)}
                  className={`px-3 sm:px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                    selectedTier === t.id
                      ? 'bg-puzzle-blue text-white'
                      : 'text-puzzle-text-secondary hover:text-puzzle-text hover:bg-puzzle-hover'
                  }`}
                >
                  <span className="mr-1">{config?.icon}</span>
                  <span className="hidden sm:inline">{t.label}</span>
                  {tierCounts[t.id] > 0 && (
                    <span className="ml-1 text-xs opacity-70">
                      (
                      {tierCounts[t.id]}
                      )
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Leaderboard Table */}
        <div className="bg-puzzle-surface rounded-lg shadow overflow-hidden">
          {/* eslint-disable-next-line no-nested-ternary */}
          {leaderboardLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-puzzle-blue" />
            </div>
          ) : leaderboard?.players?.length === 0 ? (
            <div className="text-center py-16 px-4">
              <p className="text-puzzle-text-secondary mb-2">
                No players in this tier yet.
              </p>
              <Link to="/" className="text-puzzle-blue hover:underline">
                Complete today&apos;s puzzle to earn points!
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-puzzle-text-secondary border-b bg-puzzle-muted">
                  <tr>
                    <th className="py-3 px-4 w-16">Rank</th>
                    <th className="py-3 px-4">Player</th>
                    <th className="py-3 px-4 text-center">Rating</th>
                    <th className="py-3 px-4 text-center hidden sm:table-cell">Puzzles</th>
                    <th className="py-3 px-4 text-center hidden sm:table-cell">Best</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard?.players?.map((player) => {
                    const isCurrentUser = user?.username === player.username;
                    const isInPromotionZone = leaderboard.promotionLine
                      && player.rank <= leaderboard.promotionLine;
                    const isInRelegationZone = leaderboard.relegationLine
                      && player.rank >= leaderboard.relegationLine;

                    let rowBg = '';
                    if (isCurrentUser) rowBg = 'bg-puzzle-blue/10';
                    else if (isInPromotionZone) rowBg = 'bg-green-500/5';
                    else if (isInRelegationZone) rowBg = 'bg-red-500/5';

                    return (
                      <tr
                        key={player.username}
                        className={`border-b border-puzzle-border ${rowBg}`}
                      >
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-1">
                            {getRankDisplay(player.rank)}
                            {isInPromotionZone && selectedTier !== 'diamond' && (
                              <span className="text-green-500 text-xs">&#9650;</span>
                            )}
                            {isInRelegationZone && selectedTier !== 'bronze' && (
                              <span className="text-red-500 text-xs">&#9660;</span>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-3">
                            {renderPlayerAvatar(player)}
                            <Link to={`/profile/${player.username}`} className={`font-medium hover:underline ${isCurrentUser ? 'text-puzzle-blue' : 'text-puzzle-text'}`}>
                              {player.username}
                              {isCurrentUser && <span className="ml-2 text-xs text-puzzle-blue">(You)</span>}
                            </Link>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <span className="font-bold text-lg text-puzzle-text">
                            {player.weeklyRating}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-center hidden sm:table-cell text-puzzle-text-secondary">
                          {player.weeklyCompletions}
                        </td>
                        <td className="py-4 px-4 text-center hidden sm:table-cell text-puzzle-text-secondary">
                          {player.bestDailyScore}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* User rank if outside top 50 */}
          {leaderboard?.userRank && leaderboard?.userData && (
            <div className="border-t-2 border-dashed border-puzzle-border bg-puzzle-blue/10 px-4 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-puzzle-text-secondary font-medium">
                    #
                    {leaderboard.userRank}
                  </span>
                  {renderPlayerAvatar(leaderboard.userData)}
                  <Link to={`/profile/${leaderboard.userData.username}`} className="font-medium text-puzzle-blue hover:underline">
                    {leaderboard.userData.username}
                    <span className="ml-2 text-xs">(You)</span>
                  </Link>
                </div>
                <span className="font-bold text-lg">
                  {leaderboard.userData.weeklyRating}
                  {' '}
                  pts
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Recent History */}
        {status.recentHistory?.length > 0 && (
          <div className="mt-6 bg-puzzle-surface rounded-lg shadow p-4">
            <h3 className="font-semibold text-puzzle-text mb-3">Recent Cycles</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {status.recentHistory.map((week) => {
                const weekBgClass = (() => {
                  if (week.status === 'promoted') return 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800';
                  if (week.status === 'relegated') return 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800';
                  return 'bg-puzzle-muted border border-puzzle-border';
                })();
                const weekTextClass = (() => {
                  if (week.status === 'promoted') return 'text-green-600';
                  if (week.status === 'relegated') return 'text-red-600';
                  return 'text-puzzle-text-secondary';
                })();
                return (
                  <div
                    key={week.week}
                    className={`rounded-lg p-3 text-center text-sm ${weekBgClass}`}
                  >
                    <div className="text-xs text-puzzle-text-secondary mb-1">{week.week}</div>
                    <LeagueBadge tier={week.tier} size="sm" showLabel={false} />
                    <div className="font-semibold text-puzzle-text mt-1">
                      {week.rating}
                      {' '}
                      pts
                    </div>
                    <div className={`text-xs mt-0.5 ${weekTextClass}`}>
                      {week.status === 'promoted' && '▲ Promoted'}
                      {week.status === 'relegated' && '▼ Relegated'}
                      {week.status === 'stayed' && '— Stayed'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default League;
